import { useEffect, useRef, useState } from 'react'

import type { Art } from '../lib/auszeichnung'
import { alsText, auswahlIn, lese, ordne, zeichneAus } from '../lib/reichtext'

/**
 * A writing field that can carry markup -- and every place it is read (1.69.0).
 *
 * **Why this exists.** Since 1.69.0 there are two such fields: the composer in
 * the chat and the sheet for changing a post. From use: „im Übrigen sollte das
 * auch im Änderungsmodus funktionieren." Both need the same four steps, and
 * they are easy to get subtly wrong:
 *
 * - after every input the field is read back into text (`lese()`), because the
 *   field is the truth and the state follows it -- never the other way round,
 *   that would put the cursor at the end;
 * - pasted content is taken **as text only** (`alsText()`), otherwise foreign
 *   HTML lands in the chat;
 * - a mark is put on and the field read again -- a mark that nobody reads back
 *   is lost on sending;
 * - and whether something is selected at all, because the buttons only make
 *   sense then.
 *
 * Written out twice, these four run apart -- the house has practised that
 * three times (`grabPoster()`, `Emoji`, the tab „Bearbeiten"). So they stand
 * here once, and each caller keeps only what makes it different: the composer
 * grows with its text, sends on Enter and carries a placeholder; the sheet
 * fills its height and lets Enter make a paragraph.
 *
 * @param onGelesen What the field now says, as text with characters.
 */
export function useSchreibfeld(onGelesen: (text: string) => void) {
  const feld = useRef<HTMLDivElement>(null)
  const [hatAuswahl, setHatAuswahl] = useState(false)

  /*
   * **Die Frage „ist etwas markiert?" hängt am Dokument, nicht am Feld.**
   * `selectionchange` gibt es nur dort; `auswahlIn()` entscheidet danach, ob
   * die Auswahl in *diesem* Feld liegt. Zwei Felder auf einer Seite hören
   * beide zu und beantworten die Frage jedes für sich.
   */
  useEffect(() => {
    const schauen = () => setHatAuswahl(auswahlIn(feld.current))
    document.addEventListener('selectionchange', schauen)
    return () => document.removeEventListener('selectionchange', schauen)
  }, [])

  /*
   * **Backspace before an emoji is done here and not by WebKit** (1.185.2).
   * From use, iPhone 16 Pro and 17 Pro Max with iOS 27: an emoji taken from
   * the suggestion above the keyboard, at the end, cannot be deleted; text
   * before it can. The suggestion arrives as a replacement the keyboard
   * keeps hidden state about, and that state is what a delete goes wrong on
   * (WebKit bug 265674). So when the grapheme before the caret is an emoji,
   * the delete is cancelled and the whole grapheme -- with its variation
   * selector and joiners -- is taken out by hand. Everything else stays the
   * browser's, with its undo and its corrections. Not staged here: this
   * Chromium has no suggestion bar; `markupfield.mjs` shows the hand path
   * runs and deletes exactly one grapheme.
   */
  useEffect(() => {
    const el = feld.current
    if (el === null) return
    const vorEmoji = (event: InputEvent) => {
      if (event.inputType !== 'deleteContentBackward' || event.isComposing) return
      const stelle = emojiVorCursor(el)
      if (stelle === null) return
      event.preventDefault()
      stelle.deleteContents()
      const auswahl = window.getSelection()
      auswahl?.removeAllRanges()
      auswahl?.addRange(stelle)
      liesJetzt.current()
    }
    el.addEventListener('beforeinput', vorEmoji)
    return () => el.removeEventListener('beforeinput', vorEmoji)
  }, [])

  /*
   * **Before reading, the field is put in order** (1.185.2, `ordne()`): what
   * the browser leaves behind after deleting a marked word is a style and
   * not a mark, and everything typed afterwards landed in it. Not during a
   * composition -- the word being composed lives in the node that would be
   * replaced; the next input after it does it.
   */
  function lies(event?: React.FormEvent): void {
    const el = feld.current
    if (el === null) return
    const komponiert = (event?.nativeEvent as InputEvent | undefined)?.isComposing === true
    if (!komponiert) ordne(el)
    onGelesen(lese(el))
  }
  // The listener above is set once; it reads with the callback of now.
  const liesJetzt = useRef(lies)
  liesJetzt.current = lies

  return {
    feld,
    hatAuswahl,

    /**
     * Eine Auszeichnung auf die Auswahl legen -- oder wieder wegnehmen.
     *
     * **Die Auswahl bleibt stehen.** Deshalb hängt der Knopf an `mousedown`
     * mit `preventDefault` (in `Auszeichnungsknoepfe`): ein Knopf, der den
     * Fokus nimmt, löscht die Markierung, und danach gibt es nichts mehr
     * auszuzeichnen.
     */
    lege(art: Exclude<Art, 'text'>): void {
      if (feld.current === null) return
      zeichneAus(art)
      lies()
    },

    onInput: (event: React.FormEvent) => lies(event),

    onPaste(event: React.ClipboardEvent): void {
      // Aus einer fremden Seite kommt fremdes HTML. Hier zählt der Wortlaut,
      // und sonst nichts.
      event.preventDefault()
      alsText(event.clipboardData.getData('text/plain'))
      lies()
    },

    /*
     * **Dropping is pasting by another hand** (1.187.0, review 2026-10b B7):
     * a fragment dragged from another page landed as HTML until the next
     * keystroke put the field in order. The words go in where the drop was,
     * nothing else; a dropped file is no text and stays out.
     */
    onDrop(event: React.DragEvent): void {
      event.preventDefault()
      const text = event.dataTransfer.getData('text/plain')
      if (text === '') return
      const stelle = stelleAus(event.clientX, event.clientY)
      feld.current?.focus()
      if (stelle !== null && feld.current?.contains(stelle.startContainer) === true) {
        const auswahl = window.getSelection()
        auswahl?.removeAllRanges()
        auswahl?.addRange(stelle)
      }
      alsText(text)
      lies()
    },
  }
}

/**
 * The emoji right before a collapsed caret, as a range over its whole
 * grapheme -- or `null` when there is none (then the browser deletes).
 */
function emojiVorCursor(feld: HTMLElement): Range | null {
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0 || !auswahl.isCollapsed) return null
  const cursor = auswahl.getRangeAt(0)
  const knoten = cursor.startContainer
  if (knoten.nodeType !== Node.TEXT_NODE || !feld.contains(knoten) || cursor.startOffset === 0) return null
  const davor = (knoten.textContent ?? '').slice(0, cursor.startOffset)
  const teile = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(davor)]
  const letztes = teile.at(-1)
  if (letztes === undefined || !/\p{Extended_Pictographic}/u.test(letztes.segment)) return null
  const bereich = document.createRange()
  bereich.setStart(knoten, letztes.index)
  bereich.setEnd(knoten, cursor.startOffset)
  return bereich
}

/** Where in the text a drop at this point lands, if the browser can say. */
function stelleAus(x: number, y: number): Range | null {
  const dokument = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
  }
  if (typeof dokument.caretPositionFromPoint === 'function') {
    const pos = dokument.caretPositionFromPoint(x, y)
    if (pos === null) return null
    const bereich = document.createRange()
    bereich.setStart(pos.offsetNode, pos.offset)
    return bereich
  }
  return typeof document.caretRangeFromPoint === 'function' ? document.caretRangeFromPoint(x, y) : null
}
